"use client";

import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import PhoneInputWithCountrySelect, {
  getCountryCallingCode,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import { E164Number } from "libphonenumber-js/core";
import { handleCampaignID, insertDataInNetEnquiries } from "@/lib/utils";
import { QueryParams } from "@/types/forms";
import {
  getQueryParamsFromUrl,
  googleTagManager,
} from "@/components/common/utils";
import {
  MilesHealthcareSubCategory,
  HealthcareFormHeading,
  VerticalCategory,
} from "@/interface/types";
import { usePathname } from "next/navigation";
import {
  clearFromOneDataFromLocal,
  getFormOneDataFromLocal,
} from "@/lib/edcrypt";
import { MilesAlert } from "@/lib/alerts";
import Image from "next/image";

// Custom component to display country code instead of flag
const CountryCodeDisplay = ({ country }: { country: string }) => {
  const callingCode = country ? getCountryCallingCode(country as any) : "";
  return (
    <span className="inline-block min-w-[30px] text-sm font-medium">
      +{callingCode}
    </span>
  );
};

interface Vertical {
  Accouting: "accounting";
  Healthcare: "healthcare";
}

const HealthcareGlobalForm: React.FC<{
  formType: string;
  courseId: number;
  verticalCategory: string;
  subCategory?: string;
  submitButtonName: string;
  formHeading?: string;
}> = ({
  formType,
  courseId,
  verticalCategory,
  subCategory,
  submitButtonName,
  formHeading,
}) => {
  const initialState = {
    fullname: "",
    phone: "",
    email: "",
    nationality: "",
    country_of_residence: "",
    highest_education: "",
    neet_exam_status: "",
    connectWhatsApp: true,
  };

  const btnInitalData = {
    text: submitButtonName ? submitButtonName : "Submit",
    disabled: false,
    show: true,
  };

  const [formState, setFormState] = useState({
    message: "",
    error: "",
  });

  const path = usePathname();
  const formRef = useRef<HTMLFormElement>(null);
  const [data, setData] = useState(initialState);
  const [currentStep, setCurrentStep] = useState(1);
  const [formBtnText, setFormBtnText] = useState(btnInitalData);
  const [tkId, setTkId] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const currentYear = new Date().getFullYear();

  const HEALTHCARE_LOCAL_STORE_NAME = "MILES_NEXT_F1_HEALTHCARE_REF";

  const validateField = (
    value: any,
    message: string,
    type: string,
  ): boolean => {
    const validRegex = (email: string) => {
      if (email.endsWith("@mileseducation.com")) {
        return /^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
      }
      return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
    };
    let isValid = true;

    switch (type) {
      case "empty":
        isValid = !(value === null || value === undefined || value === "");
        break;
      case "phoneLength":
        if (value) {
          isValid = isPossiblePhoneNumber(value) && isValidPhoneNumber(value);
        } else {
          isValid = false;
        }
        break;
      case "email":
        isValid = value ? validRegex(value) : false;
        break;
    }

    return isValid;
  };

  const getSuccessMessage = () => {
    return (
      <>
        <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 mx-auto mb-6 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-center mb-4 text-white">
            Thank you for reaching out!
          </h2>
          <p className="text-center text-white">
            Our representative will get in touch with you shortly.
          </p>
        </div>
      </>
    );
  };

  useEffect(() => {
    const init = async () => {
      try {
        const campaignId = handleCampaignID();
        setTkId(campaignId);
      } catch (error: any) {
        console.error(`Error fetching event data: ${error.message}`);
      }
    };

    init();
  }, []);

  useEffect(() => {
    const formSubmitted = sessionStorage.getItem("formSubmitted");

    if (formSubmitted === "true") {
      sessionStorage.removeItem("formSubmitted");
      setCurrentStep(1);
    }
  }, []);

  const handlePhoneChange = (value?: E164Number | undefined) => {
    setData((prevData) => ({
      ...prevData,
      phone: value ?? "",
    }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleChange = (
    e: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | Vertical[keyof Vertical]
    >,
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    const newValue =
      type === "checkbox" ? (e.target as HTMLInputElement)?.checked : value;

    setData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const btnLoading = () => {
    setFormBtnText((prev) => ({
      ...prev,
      text: "Submitting...",
      disabled: true,
    }));
  };

  const submitFormData = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const obj: QueryParams = localStorage?.getItem("seo_params")
        ? JSON.parse(localStorage.getItem("seo_params") ?? "")
        : { gclid: null, tk: null };

      setErrors({});
      const newErrors: { [key: string]: string } = {};
      if (
        !validateField(data.fullname, "Please enter your full name", "empty")
      ) {
        newErrors.fullname = "Please enter your full name";
      }
      if (!validateField(data.phone, "Please enter mobile number", "empty")) {
        newErrors.phone = "Please enter mobile number";
      }
      if (
        !validateField(
          data.phone,
          "Please enter your valid mobile number",
          "phoneLength",
        )
      ) {
        newErrors.phone = "Please enter your valid mobile number";
      }
      if (!validateField(data.email, "Please enter your email", "empty")) {
        newErrors.email = "Please enter your email";
      }
      if (
        !validateField(data.email, "Please enter your valid email", "email")
      ) {
        newErrors.email = "Please enter your valid email";
      }

      if (subCategory === MilesHealthcareSubCategory.NURSES) {
        if (
          !validateField(
            data.nationality,
            "Please enter your nationality",
            "empty",
          )
        ) {
          newErrors.nationality = "Please enter your nationality";
        }
        if (
          !validateField(
            data.country_of_residence,
            "Please enter your country of residence",
            "empty",
          )
        ) {
          newErrors.country_of_residence =
            "Please enter your country of residence";
        }
        if (
          !validateField(
            data.highest_education,
            "Please select your highest education",
            "empty",
          )
        ) {
          newErrors.highest_education = "Please select your highest education";
        }
      }

      if (subCategory === MilesHealthcareSubCategory.DOCTORS) {
        if (
          !validateField(
            data.neet_exam_status,
            "Please select the status of your NEET exam",
            "empty",
          )
        ) {
          newErrors.neet_exam_status =
            "Please select the status of your NEET exam";
        }
      }

      // If there are errors, set them and stop processing
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Only start loading when validation passes and API call begins
      btnLoading();

      const neetExamYearStatus =
        data.neet_exam_status === `appeared_in_${currentYear - 1}_or_earlier`
          ? currentYear - 1
          : data.neet_exam_status === `appeared_in_${currentYear}`
            ? currentYear
            : data.neet_exam_status === "yet_to_appear"
              ? currentYear + 1
              : "";

      if (true) {
        let payload = {
          first_name: data?.fullname,
          phone: data?.phone || null,
          email: data?.email || null,
          course_id: courseId,
          nationality:
            subCategory === MilesHealthcareSubCategory.NURSES
              ? data.nationality
              : "",
          year_of_graduation:
            verticalCategory === VerticalCategory.HEALTHCARE
              ? neetExamYearStatus
              : "",
          country_of_residence:
            subCategory === MilesHealthcareSubCategory.NURSES
              ? data.country_of_residence
              : "",
          highestEducation:
            subCategory === MilesHealthcareSubCategory.NURSES
              ? data.highest_education
              : "",
          whatsapp_opt_in:
            verticalCategory === VerticalCategory.HEALTHCARE
              ? data?.connectWhatsApp
                ? true
                : false
              : false,
          campaign_tk: tkId || "e3a268cf-420c-415c-a234-2825d878e688",
          whatsappAllow: 1,
          smsAllow: 1,
          courseTitle:
            verticalCategory === VerticalCategory.HEALTHCARE
              ? "doctors_pathway"
              : "",
          gcl_id: obj?.gclid,
          interested_webinar_id: null,
          register_for_webinar: true,
          coming_from: `website-${formType}`,
          page_full_url: window.location.href,
          url_page_path: path,
          user_track_id: localStorage.getItem("track")
            ? localStorage.getItem("track")
            : null,
        };

        const { data: resultData, error } =
          await insertDataInNetEnquiries(payload);
        console.warn(resultData);

        if (!error) {
          setData(initialState);
          setFormBtnText(btnInitalData);

          sessionStorage.setItem("formSubmitted", "true");

          setFormBtnText((prev) => ({
            ...prev,
            text: submitButtonName,
            disabled: false,
          }));
          setFormState({ message: "Success", error: "" });

          if (!formState.error) {
            setCurrentStep(2);
          }
          if (verticalCategory === VerticalCategory.HEALTHCARE) {
            googleTagManager("gtm_form_filled_doctors", {
              ...data,
              courseTitle: payload.courseTitle,
              course: payload.course_id,
              whatsappAllow: payload.whatsappAllow,
              smsAllow: payload.smsAllow,
              user_track_id: payload.user_track_id,
              coming_from: `website-${formType}`,
            });
          } else if (verticalCategory === VerticalCategory.MBA_AI) {
            googleTagManager("gtm_form_filled_MBA-AI", {
              ...data,
              courseTitle: payload.courseTitle,
              course: payload.course_id,
              whatsappAllow: payload.whatsappAllow,
              smsAllow: payload.smsAllow,
              user_track_id: payload.user_track_id,
              coming_from: `website-${formType}`,
            });
          }
          clearFromOneDataFromLocal(HEALTHCARE_LOCAL_STORE_NAME);
        } else {
          MilesAlert({
            text: "Something went wrong",
            type: "error",
          }).then(() => {
            setFormBtnText(btnInitalData);
          });
          setFormState({
            message: "Error",
            error: "An unexpected error occurred while adding data",
          });
        }
      } else {
        setFormBtnText(btnInitalData);
      }
    } catch (error) {
      if (error) {
        setFormState({
          message: "Error",
          error: "error",
        });
        setFormBtnText(btnInitalData);
      } else {
        setFormState({ message: "", error: "An unexpected error occurred" });
      }
    }
  };

  useEffect(() => {
    getQueryParamsFromUrl();
    (() => {
      const a = getFormOneDataFromLocal(HEALTHCARE_LOCAL_STORE_NAME);
      if (a.status) {
        setData((prev) => ({
          ...prev,
          name: a.data.fullname,
          phone: a.data.phone,
          email: a.data.email,
          nationality: a.data.nationality,
          country_of_residence: a.data.country_of_residence,
          highest_education: a.data.highest_education,
          neet_exam_status: a.data.neet_exam_status,
        }));
        setCurrentStep(2);
        setFormBtnText((prev) => ({
          ...prev,
          text: submitButtonName,
        }));
      }
    })();
  }, []);

  useEffect(() => {
    setData(initialState);
    setFormBtnText(btnInitalData);
  }, []);

  return (
    <div>
      {/* Step 1 */}
      {currentStep === 1 &&
        formState?.message === "" &&
        formState?.error === "" && (
          <div className="w-auto md:p-4 p-2 mx-auto rounded-[24px]">
            {subCategory === MilesHealthcareSubCategory.DOCTORS &&
              formHeading === HealthcareFormHeading.DOCTORS_PAGE_TOP && (
                <h2 className="text-center text-base lg:text-[18px] tracking-[-1.44px] font-semibold mb-4 text-white">
                  Join us this{" "}
                  <span className="text-[#FFBA08] text-base lg:text-[20px] font-semibold tracking-[-0.28px]">
                    Saturday
                  </span>{" "}
                  at{" "}
                  <span className="text-[#FFBA08] text-base lg:text-[20px] font-semibold tracking-[-0.28px]">
                    5:30 PM
                  </span>{" "}
                  for a live webinar!
                </h2>
              )}
            {subCategory === MilesHealthcareSubCategory.DOCTORS &&
              formHeading === HealthcareFormHeading.DOCTOR_PAGE_MIDDLE && (
                <h2 className="text-center text-base lg:text-lg 2xl:text-2xl tracking-[-1.44px] font-semibold mb-4 text-white">
                  Be a Licensed Doctor in the{" "}
                  <span className="text-[#FFBA08] text-base lg:text-2xl font-semibold tracking-[-0.28px]">
                    USA
                  </span>
                </h2>
              )}

            {subCategory === MilesHealthcareSubCategory.DOCTORS &&
              formHeading === HealthcareFormHeading.DOCTORS_PAGE_BOTTOM && (
                <h2 className="text-center text-base lg:text-2xl tracking-[-1.44px] font-semibold mb-4 text-white">
                  Be a Licensed Doctor in the{" "}
                  <span className="text-[#FFBA08] text-base lg:text-2xl font-semibold tracking-[-0.28px]">
                    USA
                  </span>
                </h2>
              )}

            {subCategory === MilesHealthcareSubCategory.NURSES && (
              <h2 className="text-center text-base lg:text-2xl tracking-[-1.44px] font-semibold mb-4 text-white">
                Land top nursing jobs in the USA
              </h2>
            )}

            {verticalCategory === VerticalCategory.MBA_AI &&
              formHeading === HealthcareFormHeading.MBA_AI && (
                <h2 className="text-xl font-semibold tracking-[-0.4px] text-center text-white mb-5">
                  Webinar on MBA in <br />
                  Artificial Intelligence
                </h2>
              )}

            <form ref={formRef} onSubmit={submitFormData}>
              <div className="mb-4">
                <label htmlFor="newuserform-fullname-field" className="sr-only">
                  Full Name
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  id="newuserform-fullname-field"
                  name="fullname"
                  value={data.fullname}
                  placeholder="Full Name*"
                  onChange={handleChange}
                  className={`appearance-none block text-sm 2xl:text-base w-full md:h-11 lg:h-11 h-[42px] ${errors.fullname ? "border-red-500 bg-[#06254C]" : verticalCategory === VerticalCategory.MBA_AI ? "bg-[#070C0F]" : "bg-[#06254C]"} text-[#ADADAD] rounded-[12px] border border-[#061A2F] tracking-[-0.14px] py-3 px-4`}
                />
                {errors.fullname && (
                  <p className="text-red-500 text-xs mt-[2px] ml-1">
                    {errors.fullname}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="phone" className="sr-only">
                  Phone Number
                </label>
                <PhoneInputWithCountrySelect
                  id="phone"
                  name="phone"
                  type="tel"
                  value={data.phone}
                  onChange={(value) =>
                    handlePhoneChange(value as E164Number | undefined)
                  }
                  placeholder="Phone Number*"
                  autoComplete="off"
                  defaultCountry="IN"
                  flagComponent={CountryCodeDisplay}
                  className={`appearance-none block text-sm w-full ${errors.phone ? "border-red-500 bg-[#06254C]" : verticalCategory === VerticalCategory.MBA_AI ? "bg-[#070C0F]" : "bg-[#06254C]"} text-[#ADADAD] md:h-11 lg:h-11 h-[42px] rounded-[12px] border border-[#061A2F] tracking-[-0.14px] py-3 px-4`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-[2px] ml-1">
                    {errors.phone}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="newuserform-email-field" className="sr-only">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="off"
                  id="newuserform-email-field"
                  value={data.email}
                  placeholder="E-mail*"
                  onChange={handleChange}
                  className={`appearance-none block text-sm 2xl:text-base w-full md:h-11 lg:h-11 h-[42px] ${errors.email ? "border-red-500 bg-[#06254C]" : verticalCategory === VerticalCategory.MBA_AI ? "bg-[#070C0F]" : "bg-[#06254C]"} text-[#ADADAD] rounded-[12px] border border-[#061A2F] tracking-[-0.14px] py-3 px-4`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-[2px] ml-1">
                    {errors.email}
                  </p>
                )}
              </div>

              {subCategory === MilesHealthcareSubCategory.NURSES && (
                <>
                  <div className="mb-4">
                    <label
                      htmlFor="newuserform-nationality-field"
                      className="sr-only"
                    >
                      Nationality
                    </label>
                    <input
                      type="text"
                      name="nationality"
                      autoComplete="off"
                      id="newuserform-nationality-field"
                      value={data.nationality}
                      placeholder="Enter your nationality*"
                      onChange={handleChange}
                      className={`appearance-none block text-sm 2xl:text-base w-full md:h-11 lg:h-11 h-[42px]  text-[#ADADAD] rounded-[12px] border  tracking-[-0.14px] py-2 px-3 ${errors.nationality ? "border-red-500 bg-[#06254C]" : "border-[#061A2F] bg-[#06254C]"}`}
                    />
                    {errors.nationality && (
                      <p className="text-red-500 text-xs mt-[2px] ml-1">
                        {errors.nationality}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="newuserform-country-field"
                      className="sr-only"
                    >
                      Country of Residence
                    </label>
                    <input
                      type="text"
                      name="country_of_residence"
                      autoComplete="off"
                      id="newuserform-country-field"
                      value={data.country_of_residence}
                      placeholder="Enter your country of residence*"
                      onChange={handleChange}
                      className={`appearance-none block text-sm 2xl:text-base w-full md:h-11 lg:h-11 h-[42px] text-[#ADADAD] rounded-[12px] border tracking-[-0.14px] py-2 px-3 ${errors.country_of_residence ? "border-red-500 bg-[#06254C]" : "border-[#061A2F] bg-[#06254C]"}`}
                    />
                    {errors.country_of_residence && (
                      <p className="text-red-500 text-xs mt-[2px] ml-1">
                        {errors.country_of_residence}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label htmlFor="highest_education" className="sr-only">
                      Highest Education
                    </label>
                    <select
                      id="highest_education"
                      title="Enter highest education"
                      name="highest_education"
                      value={data.highest_education}
                      onChange={handleChange}
                      className={`appearance-auto block text-sm 2xl:text-base w-full bg-[#06254C] text-[#ADADAD] md:h-11 lg:h-11 h-[42px] rounded-[12px] border border-[#061A2F] tracking-[-0.14px] py-2 px-3 focus:outline-none ${errors.highest_education ? "border-red-500 bg-[#06254C]" : "border-[#061A2F] bg-[#06254C]"}`}
                    >
                      <option value="" disabled>
                        Enter your highest education*
                      </option>
                      <option value="Bechelors">Bechelor's</option>
                      <option value="Masters">Master's</option>
                      <option value="Equivalent_to_bechelors">
                        Equivalent to Bechelor's
                      </option>
                    </select>
                    {errors.highest_education && (
                      <p className="text-red-500 text-xs mt-[2px] ml-1">
                        {errors.highest_education}
                      </p>
                    )}
                  </div>
                </>
              )}

              {subCategory === MilesHealthcareSubCategory.DOCTORS && (
                <div className="mb-4">
                  <label htmlFor="neet_exam_status" className="sr-only">
                    Status of your NEET Exam
                  </label>
                  <select
                    id="neet_exam_status"
                    title="Status of your NEET Exam*"
                    name="neet_exam_status"
                    value={data.neet_exam_status}
                    onChange={handleChange}
                    className={`appearance-auto block text-sm 2xl:text-base w-full bg-[#06254C] md:h-11 lg:h-11 h-[42px] rounded-[12px] border border-[#061A2F] py-2 pl-2 pr-4 text-[#ADADAD] ${errors.neet_exam_status ? "border-red-500 bg-[#06254C]" : "border-[#061A2F] bg-[#06254C]"}`}
                  >
                    <option value="" disabled>
                      Status of your NEET Exam*
                    </option>
                    <option value={`appeared_in_${currentYear - 1}_or_earlier`}>
                      Appeared in 2024 or earlier
                    </option>
                    <option value={`appeared_in_${currentYear}`}>
                      Appeared in 2025
                    </option>
                    <option value={`yet_to_appear`}>Yet to Appear</option>
                  </select>
                  {errors.neet_exam_status && (
                    <p className="text-red-500 text-xs mt-[2px] ml-1">
                      {errors.neet_exam_status}
                    </p>
                  )}
                </div>
              )}

              {verticalCategory === VerticalCategory.HEALTHCARE && (
                <div className="flex justify-start items-center text-white my-4">
                  <input
                    id="page"
                    name="connectWhatsApp"
                    type="checkbox"
                    checked={data?.connectWhatsApp}
                    onChange={handleChange}
                    className={`w-5 h-5 border-[2px] rounded-full appearance-none cursor-pointer relative mr-2 checked:bg-[#1772EA] checked:border-[#1772EA] after:content-['✓'] after:absolute after:top-1/2 after:left-1/2 after:transform after:-translate-x-1/2 after:-translate-y-1/2 after:text-[10px] after:font-bold after:opacity-0 checked:after:opacity-100 after:transition-opacity after:duration-200`}
                  />
                  <label
                    htmlFor="page"
                    className={`flex items-center md:text-[12px] text-[10px] font-medium tracking-[-0.373px] cursor-pointer`}
                  >
                    Yes, connect with me on
                    <Image
                      src="/newImages/MilesEducationWebsite-2024/whatsapp_icon_new.png"
                      alt="WhatsApp"
                      width={16}
                      height={16}
                      className="mx-1 w-4 h-4"
                    />
                    <span className={`font-semibold`}>
                      WhatsApp, Email, SMS, and Call
                    </span>
                  </label>

                  <div className="relative group ml-2">
                    <div
                      className={`w-4 h-4 flex items-center justify-center rounded-full cursor-pointer bg-white text-black font-semibold text-[10px] leading-[16px]`}
                    >
                      i
                    </div>
                    <div
                      className={`absolute z-10 hidden group-hover:block whitespace-normal w-64 text-[10px] leading-[14px] p-2 rounded-md shadow-lg bg-white text-[#030f1c] bottom-full -right-3  mb-2`}
                    >
                      By checking the ✓ box, you consent to receiving important
                      updates and relevant information about your journey with
                      Miles Education via WhatsApp, Email, SMS and Call. You can
                      always choose to opt-out in the future.
                      <div
                        className={`absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white`}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 items-center justify-center">
                <button
                  className="font-semibold bg-[#1772EA] text-white text-xs md:text-base py-[10px] md:py-[9px] w-full capitalize rounded-[24px_24px_24px_0px] hover:bg-[#1772EA]"
                  type="submit"
                  disabled={formBtnText.disabled}
                >
                  {formBtnText.text}
                </button>
              </div>
              {verticalCategory === VerticalCategory.MBA_AI && (
                <>
                  <p className="text-base text-white mt-5 text-center">
                    Happening on
                    <span className="text-[#FFBA08] font-bold">
                      {" "}
                      24th May 2025{" "}
                    </span>
                    at
                    <span className="text-[#FFBA08] font-bold"> 4PM</span>
                  </p>
                </>
              )}
            </form>
          </div>
        )}

      {/* Step 2 */}
      {currentStep === 2 && getSuccessMessage()}
    </div>
  );
};

export default HealthcareGlobalForm;
